import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { NRSLogo, NRSWordmark } from "../components/NRSLogo";
import { LangToggle, useT } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";

const INITIAL_RISK = {
  invasive: "unknown",
  invasive_type: "unknown",
  duration: "unknown",
  active_device: "unknown",
  administers_energy: "unknown",
  energy_potentially_hazardous: "unknown",
  active_diagnostic_monitoring: "unknown",
  diagnostic_high_risk: "unknown",
  active_medicinal_administration: "unknown",
  active_use_hazardous: "unknown",
  xray_image_recorder: "unknown",
  inhalation_medicinal: "unknown",
  inhalation_essential_effect: "unknown",
  integrated_diagnostic_therapeutic: "unknown",
  channels_blood_or_meds: "unknown",
  blood_bag: "unknown",
  modifies_blood_composition: "unknown",
  modifies_blood_high_risk: "unknown",
  contact_injured_skin: "unknown",
  wound_category: "unknown",
  reusable_surgical: "unknown",
  direct_contact_heart_cns_circulatory: "unknown",
  biological_effect_or_absorption: "unknown",
  rule8_specific_implant: "unknown",
  contains_medicinal: "unknown",
  medicinal_substance_ancillary: "unknown",
  contraceptive: "unknown",
  sterilises_devices: "unknown",
  final_disinfection_invasive: "unknown",
  animal_or_human_tissue: "unknown",
  tissue_origin: "unknown",
  animal_tissue_intact_skin_only: "unknown",
  contains_nanomaterial: "unknown",
  nanomaterial_exposure: "unknown",
  substance_via_orifice_or_skin: "unknown",
  substance_exposure: "unknown",
};

const SCREENING_FIELDS = [
  ["channels_blood_or_meds", "wiz.f.channels"],
  ["modifies_blood_composition", "wiz.f.modifies_blood"],
  ["contact_injured_skin", "wiz.f.injured_skin"],
  ["contains_medicinal", "wiz.f.medicinal"],
  ["contraceptive", "wiz.f.contraceptive"],
  ["sterilises_devices", "wiz.f.sterilises"],
  ["animal_or_human_tissue", "wiz.f.tissue"],
  ["contains_nanomaterial", "wiz.f.nano"],
  ["substance_via_orifice_or_skin", "demo.f.substance"],
];

const ACTIVE_FIELDS = [
  ["administers_energy", "wiz.f.energy"],
  ["active_diagnostic_monitoring", "demo.f.active_diagnostic"],
  ["active_medicinal_administration", "demo.f.active_admin"],
  ["xray_image_recorder", "demo.f.xray"],
  ["inhalation_medicinal", "demo.f.inhalation"],
  ["integrated_diagnostic_therapeutic", "demo.f.integrated_diagnostic"],
];

const DETAIL_FIELDS = [
  ["blood_bag", "demo.f.blood_bag"],
  ["modifies_blood_high_risk", "demo.f.blood_treatment"],
  ["wound_category", "demo.f.wound_category", "wound"],
  ["reusable_surgical", "demo.f.reusable_surgical"],
  ["direct_contact_heart_cns_circulatory", "demo.f.critical_contact"],
  ["biological_effect_or_absorption", "demo.f.bio_effect"],
  ["rule8_specific_implant", "demo.f.specific_implant"],
  ["energy_potentially_hazardous", "demo.f.energy_hazard"],
  ["diagnostic_high_risk", "demo.f.diagnostic_high_risk"],
  ["active_use_hazardous", "demo.f.active_hazard"],
  ["inhalation_essential_effect", "demo.f.inhalation_essential"],
  ["final_disinfection_invasive", "demo.f.final_disinfection"],
  ["animal_tissue_intact_skin_only", "demo.f.intact_skin_only"],
  ["medicinal_substance_ancillary", "demo.f.medicinal_ancillary"],
  ["tissue_origin", "demo.f.tissue_origin", "tissue"],
  ["nanomaterial_exposure", "demo.f.nano_exposure", "nano"],
  ["substance_exposure", "demo.f.substance_exposure", "substance"],
];

function optionList(t, type = "yesNo") {
  if (type === "yesNo") return [
    ["unknown", t("demo.option.unknown")],
    ["yes", t("wiz.yes")],
    ["no", t("wiz.no")],
  ];
  const values = {
    invasive: [["unknown", "demo.option.unknown"], ["none", "demo.option.none"], ["orifice", "wiz.it.orifice"], ["surgical", "wiz.it.surgical"]],
    duration: [["unknown", "demo.option.unknown"], ["transient", "wiz.dur.transient"], ["short_term", "wiz.dur.short"], ["long_term", "wiz.dur.long"]],
    purpose: [["unknown", "demo.option.unknown"], ["diagnosis", "wiz.sp.diagnosis"], ["therapy", "wiz.sp.therapy"], ["monitoring", "wiz.sp.monitoring"], ["admin", "wiz.sp.admin"], ["none", "wiz.sp.none"]],
    impact: [["unknown", "demo.option.unknown"], ["high", "demo.impact.high"], ["medium", "demo.impact.medium"], ["low", "demo.impact.low"]],
    wound: [["unknown", "demo.option.unknown"], ["mechanical_barrier", "demo.wound.barrier"], ["superficial", "demo.wound.superficial"], ["deep_secondary_intention", "demo.wound.deep"]],
    nano: [["unknown", "demo.option.unknown"], ["none", "demo.nano.none"], ["high", "demo.nano.high"], ["medium", "demo.nano.medium"], ["low", "demo.nano.low"]],
    substance: [["unknown", "demo.option.unknown"], ["systemic", "demo.substance.systemic"], ["stomach_lower_gi", "demo.substance.gi"], ["skin_nasal_oral", "demo.substance.local_sites"], ["other_local", "demo.substance.other"]],
    tissue: [["unknown", "demo.option.unknown"], ["human", "demo.tissue.human"], ["animal", "demo.tissue.animal"]],
  };
  return (values[type] || []).map(([value, key]) => [value, t(key)]);
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold leading-relaxed text-[var(--text)]">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testid}
        className="mt-1 w-full rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text)]"
      >
        {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
      </select>
    </label>
  );
}

export default function DemoClassification() {
  const t = useT();
  const { user, logout } = useAuth();
  const [productName, setProductName] = useState("");
  const [intendedPurpose, setIntendedPurpose] = useState("");
  const [intendedUsers, setIntendedUsers] = useState("");
  const [software, setSoftware] = useState({
    is_software: "unknown",
    software_purpose: "unknown",
    decision_impact: "unknown",
  });
  const [risk, setRisk] = useState(INITIAL_RISK);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const yesNoOptions = optionList(t);
  const invitedViewer = user?.workspace_role === "viewer" && user?.workspace_id !== user?.id;
  if (invitedViewer) return <Navigate to="/dashboard" replace />;
  if (user?.account_tier !== "demo" && user?.role !== "admin") return <Navigate to="/dashboard" replace />;

  function updateRisk(key, value) {
    setRisk((current) => ({ ...current, [key]: value }));
    setResult(null);
  }

  function updateSoftware(key, value) {
    setSoftware((current) => ({ ...current, [key]: value }));
    setResult(null);
  }

  async function classify(event) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setResult(null);
    try {
      const response = await api.post("/demo/classify", {
        device_identity: { name: productName.trim() },
        intended_purpose: { description: intendedPurpose.trim() },
        user_profile: { intended_users: intendedUsers.trim() },
        software_ai: software,
        risk_profile: risk,
        lang: localStorage.getItem("rg_lang") || "en",
      });
      setResult(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  const yesNoSelect = (key, labelKey) => (
    <SelectField
      key={key}
      label={t(labelKey)}
      value={risk[key]}
      onChange={(value) => updateRisk(key, value)}
      options={yesNoOptions}
      testid={`demo-${key}`}
    />
  );

  function extraField([key, labelKey, optionType]) {
    return (
      <SelectField
        key={key}
        label={t(labelKey)}
        value={risk[key]}
        onChange={(value) => updateRisk(key, value)}
        options={optionList(t, optionType || "yesNo")}
        testid={`demo-${key}`}
      />
    );
  }

  return (
    <main className="min-h-screen bg-[var(--bg)] px-4 py-5 text-[var(--text)] sm:px-6">
      <div className="mx-auto max-w-5xl">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border)] pb-4">
          <Link to="/" className="flex items-center gap-2" aria-label="MDRFlow — European Medical Device Regulatory Platform">
            <NRSLogo size={28} />
            <NRSWordmark />
          </Link>
          <div className="flex items-center gap-3">
            <LangToggle />
            <span className="hidden text-xs text-[var(--text-2)] sm:inline">{user?.email}</span>
            <button type="button" onClick={async () => { await logout(); window.location.href = "/"; }} className="text-xs font-semibold text-[var(--accent-text)] hover:underline">
              {t("nav.logout")}
            </button>
          </div>
        </header>

        <section className="mb-5">
          <div className="text-xs font-mono uppercase tracking-wider text-cyan-500">{t("demo.eyebrow")}</div>
          <h1 className="mt-2 text-2xl font-extrabold sm:text-3xl">{t("demo.title")}</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[var(--text-2)]">{t("demo.subtitle")}</p>
        </section>

        <form onSubmit={classify} className="space-y-5">
          <section className="grid-card space-y-4 p-4 sm:p-6">
            <h2 className="font-bold">{t("demo.device_basics")}</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <TextField label={t("wiz.f.device_name")} value={productName} onChange={(value) => { setProductName(value); setResult(null); }} testid="demo-product-name" required />
              <TextField label={t("wiz.f.users")} value={intendedUsers} onChange={setIntendedUsers} testid="demo-intended-users" />
              <div className="md:col-span-2">
                <TextField label={t("wiz.f.intended_purpose")} value={intendedPurpose} onChange={(value) => { setIntendedPurpose(value); setResult(null); }} testid="demo-purpose" required textarea />
              </div>
            </div>
          </section>

          <section className="grid-card space-y-4 p-4 sm:p-6">
            <h2 className="font-bold">{t("demo.primary_characteristics")}</h2>
            <p className="text-xs leading-relaxed text-[var(--text-2)]">{t("demo.unknown_note")}</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {yesNoSelect("invasive", "wiz.f.invasive")}
              <SelectField label={t("wiz.f.invasive_type")} value={risk.invasive_type} onChange={(value) => updateRisk("invasive_type", value)} options={optionList(t, "invasive")} testid="demo-invasive-type" />
              <SelectField label={t("wiz.f.duration")} value={risk.duration} onChange={(value) => updateRisk("duration", value)} options={optionList(t, "duration")} testid="demo-duration" />
              {yesNoSelect("active_device", "wiz.f.active")}
              <SelectField label={t("wiz.f.is_sw")} value={software.is_software} onChange={(value) => updateSoftware("is_software", value)} options={yesNoOptions} testid="demo-software" />
              <SelectField label={t("wiz.f.sw_purpose")} value={software.software_purpose} onChange={(value) => updateSoftware("software_purpose", value)} options={optionList(t, "purpose")} testid="demo-software-purpose" />
              <SelectField label={t("wiz.f.impact")} value={software.decision_impact} onChange={(value) => updateSoftware("decision_impact", value)} options={optionList(t, "impact")} testid="demo-software-impact" />
            </div>
          </section>

          <section className="grid-card space-y-4 p-4 sm:p-6">
            <h2 className="font-bold">{t("demo.rule_screening")}</h2>
            <p className="text-xs leading-relaxed text-[var(--text-2)]">{t("demo.rule_screening_help")}</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {SCREENING_FIELDS.map(([key, labelKey]) => yesNoSelect(key, labelKey))}
            </div>
            {risk.active_device === "yes" && (
              <div className="border-t border-[var(--border)] pt-4">
                <h3 className="mb-3 text-sm font-semibold">{t("demo.active_rules")}</h3>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {ACTIVE_FIELDS.map(([key, labelKey]) => yesNoSelect(key, labelKey))}
                </div>
              </div>
            )}
            <details className="rounded-sm border border-[var(--border)] p-3">
              <summary className="cursor-pointer text-sm font-semibold">{t("demo.advanced_title")}</summary>
              <p className="my-3 text-xs leading-relaxed text-[var(--text-2)]">{t("demo.advanced_help")}</p>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {DETAIL_FIELDS.map(extraField)}
              </div>
            </details>
          </section>

          {error && <div role="alert" className="rounded-sm border border-red-500/40 bg-red-950/20 p-3 text-sm text-red-400">{error}</div>}
          <button type="submit" disabled={submitting || !productName.trim() || !intendedPurpose.trim()} data-testid="demo-classify-submit" className="btn-primary w-full py-3 disabled:opacity-50 sm:w-auto sm:px-8">
            {submitting ? t("demo.running") : t("demo.run")}
          </button>
        </form>

        {result && (
          <section className="grid-card mt-6 space-y-4 border-l-4 border-cyan-500 p-4 sm:p-6" data-testid="demo-classification-result">
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-cyan-500">{t("demo.result_heading")}</div>
              {result.suggested_class ? (
                <h2 className="mt-2 text-2xl font-extrabold">
                  {t("demo.suggested_class")} <span className="text-cyan-500">{result.suggested_class}</span>
                </h2>
              ) : (
                <h2 className="mt-2 text-xl font-bold">{t("demo.insufficient")}</h2>
              )}
              <p className="mt-2 text-sm text-[var(--text-2)]">{t(result.needs_human_review ? "demo.needs_review" : "demo.preliminary")}</p>
            </div>
            {result.rule_triggers?.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-semibold">{t("demo.rationale")}</h3>
                <ul className="space-y-1 text-sm">
                  {result.rule_triggers
                    .filter((item) => item.result === result.suggested_class)
                    .slice(0, 2)
                    .map((item, index) => (
                      <li key={`${item.rule}-${index}`}>Rule {item.rule}: {item.title}</li>
                    ))}
                </ul>
                <details className="mt-3 rounded-sm border border-[var(--border)] p-3">
                  <summary className="cursor-pointer text-sm font-semibold">{t("demo.all_triggered_rules")}</summary>
                  <ul className="mt-3 space-y-1 text-sm">
                    {result.rule_triggers.map((item, index) => (
                      <li key={`${item.rule}-${index}`}>Rule {item.rule}: {item.title} — {item.result}</li>
                    ))}
                  </ul>
                </details>
              </div>
            )}
            {result.review_items?.length > 0 && (
              <div className="rounded-sm border border-amber-500/40 bg-amber-950/10 p-3">
                <h3 className="text-sm font-semibold text-amber-500">{t("demo.missing_review")}</h3>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-[var(--text-2)]">
                  {result.review_items.map((item, index) => <li key={`${index}-${item}`}>{item}</li>)}
                </ul>
              </div>
            )}
            <p className="text-xs leading-relaxed text-[var(--text-2)]">{result.disclaimer}</p>
            <a href={result.annex_viii_source || "https://eur-lex.europa.eu/eli/reg/2017/745/anx_8/oj"} target="_blank" rel="noreferrer" className="text-xs text-[var(--accent-text)] underline underline-offset-2">{t("demo.annex_source")}</a>
            <div className="flex flex-wrap gap-3 border-t border-[var(--border)] pt-4">
              <a href="/#pricing" className="btn-primary inline-flex items-center px-4 py-2 text-sm">{t("demo.view_plans")}</a>
              <a href="https://nextregsolution.com/contacto" target="_blank" rel="noreferrer" className="inline-flex items-center border border-[var(--border)] px-4 py-2 text-sm font-semibold text-[var(--text)]">{t("home.pricing.contactUs")}</a>
            </div>
          </section>
        )}
      </div>
    </main>
  );
}

function TextField({ label, value, onChange, testid, required = false, textarea = false }) {
  const Control = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[var(--text)]">{label}</span>
      <Control
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        data-testid={testid}
        rows={textarea ? 4 : undefined}
        className="mt-1 w-full rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text)]"
      />
    </label>
  );
}
